import { useEffect, useState } from 'react';
import { normalizeMarkdownCallouts, stripMarkdownBackLink } from '../../Markdown';

interface UseMarkdownDocResult {
  markdown: string | null;
  isLoading: boolean;
  hasError: boolean;
}

export function useMarkdownDoc(docPath: string | null): UseMarkdownDocResult {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!docPath) {
      setMarkdown(null);
      setIsLoading(false);
      setHasError(true);
      return undefined;
    }
    const markdownPath = docPath;

    const abortController = new AbortController();
    let isDisposed = false;

    async function loadMarkdownDoc() {
      setIsLoading(true);
      setHasError(false);
      setMarkdown(null);

      try {
        const response = await fetch(markdownPath, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load article from ${markdownPath}`);
        }

        const text = await response.text();

        if (isDisposed) {
          return;
        }

        const normalizedMarkdown = normalizeMarkdownCallouts(
          stripMarkdownBackLink(text),
        );
        setMarkdown(normalizedMarkdown);
      } catch (_error) {
        if (abortController.signal.aborted || isDisposed) {
          return;
        }
        setHasError(true);
      } finally {
        if (!abortController.signal.aborted && !isDisposed) {
          setIsLoading(false);
        }
      }
    }

    loadMarkdownDoc();

    return () => {
      isDisposed = true;
      abortController.abort();
    };
  }, [docPath]);

  return {
    markdown,
    isLoading,
    hasError,
  };
}
